/**
 * Oct 20 founder addendum S2-C — the three competency measurements, read from the
 * STORED overall `technician_diagnostic_stats` row (never recomputed in the browser:
 * the figures shown are the figures the score was derived from, so they reconcile
 * with the displayed score by construction — DB CHECK
 * `technician_diagnostic_stats_s2_reconciles`).
 *
 *   (1) Actual Diagnostic Activity Composition — real job counts; weights/cap never touch it.
 *   (2) Uncapped Weighted Evidence Contribution — after the 0.05 weight and quality, before the cap.
 *   (3) Final Credited Competency Contribution — after the cap; the score's parts.
 *
 * Pure. The formula itself lives in supabase/functions/_shared/diagnosticEvidenceScoring.ts.
 */
import type { TechnicianDiagnosticStats } from '@/types/diagnosticIntelligence';

export interface CompetencyAnalyticsView {
  scoringVersion: number;
  windowDays: number | null;
  inspectionWeight: number;
  inspectionCap: number;
  inspectionCreditEnabled: boolean;
  activity: {
    inspectionJobs: number;
    dedicatedJobs: number;
    combinedJobs: number;
    /** 0–100, null when there is no classified activity. */
    inspectionPct: number | null;
    dedicatedPct: number | null;
    unclassifiedJobs: number;
  };
  uncapped: {
    dedicatedScored: number;
    inspectionScored: number;
    diagnosticUnits: number;
    inspectionUnits: number;
    diagnosticQuality: number | null;
    inspectionQuality: number | null;
    /** Quality-adjusted contribution shares, 0–100; null when nothing to share. */
    inspectionShare: number | null;
    dedicatedShare: number | null;
  };
  credited: {
    inspectionUnits: number;
    unitsRemovedByCap: number;
    capApplied: boolean;
    capReason: string;
    inspectionShare: number | null;
    dedicatedShare: number | null;
    diagnosticPoints: number;
    inspectionPoints: number;
    neutralPoints: number;
    score: number;
    /** Inspection points as % of the final score. */
    inspectionPctOfFinal: number;
  };
  /** CORE-I :124/:49 — inspection activity alone never establishes diagnostic competency. */
  establishedByDedicatedEvidence: boolean;
  recalculation: {
    at: string | null;
    reason: string | null;
    kind: string | null;
    previousVersion: number | null;
    previousScore: number | null;
  };
}

const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));
const n0 = (v: unknown): number => num(v) ?? 0;

/** `null` for a row produced before scoring version 2 (no breakdown exists for it yet). */
export function competencyAnalyticsFromRow(row: TechnicianDiagnosticStats | null | undefined): CompetencyAnalyticsView | null {
  if (!row || num(row.scoring_version) === null || (num(row.scoring_version) as number) < 2) return null;
  const policy = (row.scoring_policy ?? {}) as Record<string, unknown>;
  const inspectionJobs = n0(row.inspection_jobs);
  const dedicatedJobs = n0(row.dedicated_diagnostic_jobs);
  const combined = inspectionJobs + dedicatedJobs;
  const inspShareU = num(row.inspection_share_uncapped);
  const inspShareC = num(row.inspection_share_credited);
  const inspUnits = n0(row.inspection_units_uncapped);
  const inspCredited = n0(row.inspection_units_credited);
  const d = n0(row.score_diagnostic_points);
  const i = n0(row.score_inspection_points);
  const n = n0(row.score_neutral_points);
  const score = n0(row.diagnostic_competency_score);
  return {
    scoringVersion: num(row.scoring_version) as number,
    windowDays: num(row.evidence_window_days),
    inspectionWeight: num(policy.inspection_weight) ?? 0.05,
    inspectionCap: num(policy.inspection_cap) ?? 0.1,
    inspectionCreditEnabled: policy.inspection_credit_enabled !== false,
    activity: {
      inspectionJobs,
      dedicatedJobs,
      combinedJobs: combined,
      inspectionPct: combined > 0 ? (inspectionJobs / combined) * 100 : null,
      dedicatedPct: combined > 0 ? (dedicatedJobs / combined) * 100 : null,
      unclassifiedJobs: n0(row.unclassified_jobs),
    },
    uncapped: {
      dedicatedScored: n0(row.dedicated_diagnostic_scored),
      inspectionScored: n0(row.inspection_scored),
      diagnosticUnits: n0(row.diagnostic_units),
      inspectionUnits: inspUnits,
      diagnosticQuality: num(row.diagnostic_quality),
      inspectionQuality: num(row.inspection_quality),
      inspectionShare: inspShareU,
      dedicatedShare: inspShareU === null ? null : 100 - inspShareU,
    },
    credited: {
      inspectionUnits: inspCredited,
      unitsRemovedByCap: Math.max(0, inspUnits - inspCredited),
      capApplied: (row.cap_reason ?? 'none') !== 'none',
      capReason: row.cap_reason ?? 'none',
      inspectionShare: inspShareC,
      dedicatedShare: inspShareC === null ? null : 100 - inspShareC,
      diagnosticPoints: d,
      inspectionPoints: i,
      neutralPoints: n,
      score,
      inspectionPctOfFinal: score > 0 ? (i / score) * 100 : 0,
    },
    establishedByDedicatedEvidence: n0(row.dedicated_diagnostic_scored) > 0,
    recalculation: {
      at: row.scoring_recalculated_at ?? null,
      reason: row.last_recalculation_reason ?? null,
      kind: row.last_recalculation_kind ?? null,
      previousVersion: num(row.previous_scoring_version),
      previousScore: num(row.previous_competency_score),
    },
  };
}

/** Is there a v2 row that says no dedicated diagnostic evidence exists? (legacy rows: unknown → false) */
export function lacksDedicatedDiagnosticEvidence(row: TechnicianDiagnosticStats | null | undefined): boolean {
  const v = competencyAnalyticsFromRow(row);
  return v !== null && !v.establishedByDedicatedEvidence;
}

export const fmtPct = (v: number | null, digits = 1): string => (v === null ? '—' : `${v.toFixed(digits)}%`);
export const fmtNum = (v: number | null, digits = 2): string => (v === null ? '—' : v.toFixed(digits));

export function capReasonText(reason: string, capPct: number): string {
  switch (reason) {
    case 'no_dedicated_diagnostic_evidence':
      return 'No dedicated diagnostic evidence yet, so inspection evidence earns no credit: it can only ever be a minor part of a competency that dedicated diagnosis establishes.';
    case 'weight_share':
      return `Inspection evidence was limited to ${capPct}% of the credited evidence weight.`;
    case 'score_share':
      return `Inspection evidence was limited to ${capPct}% of the credited score.`;
    case 'credit_disabled':
      return 'Inspection credit is switched off; inspections are shown as activity only.';
    default:
      return `Below the ${capPct}% cap — nothing was removed.`;
  }
}

export function recalculationText(r: CompetencyAnalyticsView['recalculation'], version: number): string | null {
  if (!r.reason || r.reason === 'initial' || r.reason === 'new_evidence') return null;
  const prev = r.previousScore === null ? '' : ` (previous score ${r.previousScore.toFixed(0)} under scoring version ${r.previousVersion ?? 1})`;
  if (r.reason === 'scoring_version_change') {
    return `Recalculated under scoring version ${version}${prev}. This corrects how the score is computed; it is not a change in performance.`;
  }
  return `Recalculated after a correction to how evidence was classified${prev}. This corrects the computation; it is not a change in performance.`;
}
